import React, { useCallback, useEffect, useState } from 'react';
import {
  getSharedQuizDetail,
  updateSharedQuiz,
  getQuizLeaderboard,
  SharedQuizAdminListItem,
  SharedQuizWritePayload,
  LeaderboardEntry,
  getQuizParticipants,
  setParticipantExtraAttempts,
  QuizParticipant,
} from '../../Game/api/gameApi';
import QuestionPicker from './QuestionPicker';

interface CustomQuizViewModalProps {
  quizId: string;
  onClose: () => void;
  onSaved: () => void;
}

const CustomQuizViewModal: React.FC<CustomQuizViewModalProps> = ({ quizId, onClose, onSaved }) => {
  const [activeTab, setActiveTab] = useState<'details' | 'leaderboard' | 'participants'>('details');
  const [quiz, setQuiz] = useState<SharedQuizAdminListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Edit form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rounds, setRounds] = useState(1);
  const [questionsPerRound, setQuestionsPerRound] = useState(5);
  const [isActive, setIsActive] = useState(true);
  const [limitAttempts, setLimitAttempts] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);

  const [participants, setParticipants] = useState<QuizParticipant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  const [editingAttemptsFor, setEditingAttemptsFor] = useState<number | null>(null);
  const [attemptsDraft, setAttemptsDraft] = useState('');

  const loadQuiz = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const detail = await getSharedQuizDetail(quizId);
      setQuiz(detail);
      setTitle(detail.title);
      setDescription(detail.description);
      setRounds(detail.rounds);
      setQuestionsPerRound(detail.questions_per_round);
      setIsActive(detail.is_active);
      setLimitAttempts(detail.max_attempts_per_user !== null);
      setMaxAttempts(detail.max_attempts_per_user ?? 1);
      setSelectedIds(new Set(detail.selected_question_ids));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load custom game');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    loadQuiz();
  }, [loadQuiz]);

  const loadLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const res = await getQuizLeaderboard(quizId, 999, 'all_time', 'score');
      setLeaderboard(res.results);
    } catch (err) {
      setLeaderboardError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  }, [quizId]);

  const handleExportLeaderboard = () => {
    if (leaderboard.length === 0) return;
  
    const headers = ['Rank', 'Name', 'Email', 'Score', 'Correct', 'Total Questions', 'Ended At'];
    const rows = leaderboard.map((entry, idx) => [
      idx + 1,
      entry.display_name,
      entry.email ?? '',
      entry.score,
      entry.questions_correct ?? '',
      entry.questions_total ?? '',
      entry.ended_at ?? '',
    ]);
  
    const escapeCsvField = (value: string | number) => {
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
  
    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvField).join(','))
      .join('\n');
  
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeTitle = (quiz?.title || 'custom-quiz').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const dateStr = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `${safeTitle}-leaderboard-${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (activeTab === 'leaderboard' && leaderboard.length === 0 && !leaderboardLoading) {
      loadLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadParticipants = useCallback(async () => {
    setParticipantsLoading(true);
    setParticipantsError(null);
    try {
      const res = await getQuizParticipants(quizId);
      setParticipants(res.results);
    } catch (err) {
      setParticipantsError(err instanceof Error ? err.message : 'Failed to load participants');
    } finally {
      setParticipantsLoading(false);
    }
  }, [quizId]);
  
  useEffect(() => {
    if (activeTab === 'participants' && participants.length === 0 && !participantsLoading) {
      loadParticipants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  const handleSaveExtraAttempts = async (userId: number) => {
    const value = parseInt(attemptsDraft, 10);
    if (isNaN(value) || value < 0) return;
    try {
      await setParticipantExtraAttempts(quizId, userId, value);
      setEditingAttemptsFor(null);
      await loadParticipants();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update attempts');
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setSaveError('Title is required.');
      return;
    }
    if (selectedIds.size === 0) {
      setSaveError('Select at least one question.');
      return;
    }

    const payload: SharedQuizWritePayload = {
      title: title.trim(),
      description: description.trim(),
      rounds,
      questions_per_round: questionsPerRound,
      is_active: isActive,
      max_attempts_per_user: limitAttempts ? maxAttempts : null,
      selected_question_ids: Array.from(selectedIds),
    };

    try {
      setSaving(true);
      setSaveError(null);
      await updateSharedQuiz(quizId, payload);
      await loadQuiz();
      setIsEditing(false);
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save custom game');
    } finally {
      setSaving(false);
    }
  };

  const baseInputCls = `w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{quiz?.title || 'Custom Game'}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sub-nav */}
        <div className="border-b px-6 flex-shrink-0">
          <nav className="flex space-x-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 border-b-2 text-sm font-medium ${
                activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`py-3 border-b-2 text-sm font-medium ${
                activeTab === 'leaderboard' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Leaderboard
            </button>
            <button
              onClick={() => setActiveTab('participants')}
              className={`py-3 border-b-2 text-sm font-medium ${
                activeTab === 'participants' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Participants
            </button>
          </nav>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-500">Loading...</span>
            </div>
          ) : loadError ? (
            <p className="text-red-600 text-center py-16">{loadError}</p>
          ) : activeTab === 'details' ? (
            !isEditing ? (
              // ── Read-only details ──
              <div className="space-y-4">
                <DetailRow label="Title" value={quiz?.title || '-'} />
                <DetailRow label="Description" value={quiz?.description || 'No description'} />
                <DetailRow label="Join Code" value={quiz?.join_code || '-'} mono />
                <DetailRow label="Rounds x Questions per Round" value={`${quiz?.rounds} x ${quiz?.questions_per_round}`} />
                <DetailRow label="Question Pool Size" value={`${quiz?.question_count ?? 0} question(s)`} />
                <DetailRow label="SDGs Covered" value={quiz?.included_sdgs?.join(', ') || 'None'} />
                <DetailRow
                  label="Max Attempts per User"
                  value={quiz?.max_attempts_per_user === null || quiz?.max_attempts_per_user === undefined
                    ? 'Unlimited'
                    : String(quiz.max_attempts_per_user)}
                />
                <DetailRow label="Status" value={quiz?.is_active ? 'Active' : 'Inactive'} />
                <DetailRow label="Created By" value={quiz?.created_by_username || 'N/A'} />

                <div className="pt-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              // ── Edit form ──
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={baseInputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={`${baseInputCls} resize-y`} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rounds</label>
                    <input type="number" min={1} value={rounds} onChange={(e) => setRounds(Math.max(1, Number(e.target.value)))} className={baseInputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Questions per Round</label>
                    <input type="number" min={1} value={questionsPerRound} onChange={(e) => setQuestionsPerRound(Math.max(1, Number(e.target.value)))} className={baseInputCls} />
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                    <input type="checkbox" checked={limitAttempts} onChange={(e) => setLimitAttempts(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
                    Limit attempts per user
                  </label>
                  {limitAttempts && (
                    <input type="number" min={1} value={maxAttempts} onChange={(e) => setMaxAttempts(Math.max(1, Number(e.target.value)))} className={`${baseInputCls} mt-1`} />
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
                    Active
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Pool *</label>
                  <QuestionPicker selectedIds={selectedIds} onChange={setSelectedIds} />
                </div>

                {saveError && <p className="text-sm text-red-600">{saveError}</p>}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => { setIsEditing(false); setSaveError(null); }}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )
          ) : activeTab === 'leaderboard' ? (
            // ── Leaderboard tab ──
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-500">Top {leaderboard.length} attempt(s) shown</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleExportLeaderboard}
                    disabled={leaderboard.length === 0}
                    className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Export
                  </button>
                  <button
                    onClick={loadLeaderboard}
                    disabled={leaderboardLoading}
                    className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {leaderboardLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>

              {leaderboardError ? (
                <p className="text-red-600 text-center py-8">{leaderboardError}</p>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto divide-y divide-gray-100">
                  {leaderboard.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">
                      {leaderboardLoading ? 'Loading...' : 'No attempts yet.'}
                    </p>
                  ) : (
                    leaderboard.map((entry, idx) => (
                      <div key={entry.session_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="flex items-center gap-3">
                          <span className="w-6 text-gray-400">{idx + 1}</span>
                          <span className="min-w-0">
                            <span className="text-gray-900 block truncate">{entry.display_name}</span>
                            {entry.email && <span className="text-xs text-gray-400 block truncate">{entry.email}</span>}
                          </span>
                        </span>
                        <span className="flex flex-col items-end leading-tight">
                          <span className="font-semibold text-gray-900">{entry.score}</span>
                          {entry.questions_total ? (
                            <span className="text-xs text-gray-400">{entry.questions_correct}/{entry.questions_total}</span>
                          ) : null}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            // ── Participants tab ──
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-500">{participants.length} participant(s)</p>
                <button
                  onClick={loadParticipants}
                  disabled={participantsLoading}
                  className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {participantsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {participantsError ? (
                <p className="text-red-600 text-center py-8">{participantsError}</p>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto divide-y divide-gray-100">
                  {participants.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">
                      {participantsLoading ? 'Loading...' : 'No one has attempted this quiz yet.'}
                    </p>
                  ) : (
                    participants.map((p) => (
                      <div key={p.user_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <div className="min-w-0">
                          <p className="text-gray-900 font-medium truncate">{p.display_name}</p>
                          <p className="text-xs text-gray-400 truncate">{p.email}</p>
                          <p className="text-xs text-gray-400">
                            Best score: {p.best_score} · Used: {p.attempts_used}
                            {p.attempts_allowed !== null ? ` / ${p.attempts_allowed}` : ' (unlimited)'}
                          </p>
                        </div>
                        {editingAttemptsFor === p.user_id ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <input
                              type="number"
                              min={0}
                              value={attemptsDraft}
                              onChange={(e) => setAttemptsDraft(e.target.value)}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveExtraAttempts(p.user_id)}
                              className="text-blue-600 hover:underline text-xs font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingAttemptsFor(null)}
                              className="text-gray-400 hover:text-gray-600 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingAttemptsFor(p.user_id);
                              setAttemptsDraft(String(p.extra_attempts));
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium flex-shrink-0"
                          >
                            +{p.extra_attempts} bonus attempt — Edit
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`text-sm text-gray-900 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);

export default CustomQuizViewModal;